/**
 * Governance change-request decision actions (CT-5095) — the Gov Review footer.
 *
 * Contract: My Work API (`my_work.yaml`), base `/v1/my-work`, all POST + camelCase.
 * Workspace context + the calling user come from the token — never in the body.
 *
 *   - Approve / Reject a change request → POST /v1/my-work/change-request/resolve
 *       body { changeRequestUuid, action: 'approve' | 'reject' }, response { success }.
 *       NOTE (verified live 2026-09-03): `reject` works (200 → CR REJECTED);
 *       `approve` is currently BROKEN on the backend (500 SYS001, no effect) —
 *       tracked in CT-4867. The call is wired to contract; the error surfaces as a
 *       toast until backend is fixed.
 *   - Correct (counter-propose) → POST /v1/my-work/change-request/create-on-change-request
 *       body { changeRequestUuid, suggestedText?, rationale?, riskLevel? } — creates a
 *       child change request with the corrected text.
 *
 * Errors follow the GS envelope (400 validation, 404 CHANGE_REQUEST_NOT_FOUND,
 * 409 already resolved, 500) — normalized by {@link postGsAction}.
 */

import { postGsAction } from './gsAction.js';

/**
 * Approve or reject a change request (Gov Review "Submit review").
 *
 * `reason` (CT-5334, Reject only) is NOT in the documented request schema —
 * the backend's `ResolveChangeRequestRequest` only declares `changeRequestUuid`
 * + `action`. Sent anyway: Pydantic ignores unknown fields by default here (no
 * `extra=forbid`), so this is forward-compatible the moment backend adds
 * storage for it, with zero risk to the calls that work today.
 * @param {{ changeRequestUuid: string, action: 'approve'|'reject', reason?: string }} params
 * @returns {Promise<Object|null>} unwrapped response, or `null` when ids are missing.
 */
export async function resolveChangeRequest({ changeRequestUuid, action, reason } = {}) {
  if (!changeRequestUuid || !action) return null;
  const payload = { changeRequestUuid, action };
  if (reason) payload.reason = reason;
  return postGsAction(
    '/v1/my-work/change-request/resolve',
    payload,
    'Failed to resolve change request',
  );
}

/**
 * Counter-propose a correction on a change request (Gov Review "Correct").
 * @param {{ changeRequestUuid: string, suggestedText?: string, rationale?: string,
 *           riskLevel?: 'Low'|'Medium'|'High' }} params
 * @returns {Promise<Object|null>} unwrapped response, or `null` when the uuid is missing.
 */
export async function createChangeOnChangeRequest({
  changeRequestUuid,
  suggestedText,
  rationale,
  riskLevel,
} = {}) {
  if (!changeRequestUuid) return null;
  const payload = { changeRequestUuid };
  if (suggestedText != null) payload.suggestedText = suggestedText;
  if (rationale != null) payload.rationale = rationale;
  if (riskLevel) payload.riskLevel = riskLevel;
  return postGsAction(
    '/v1/my-work/change-request/create-on-change-request',
    payload,
    'Failed to submit correction',
  );
}
